import {
    BusinessObjectPart,
    BusinessObjectPartContext,
    FeatureFlagsRulesServiceBase,
    ModelBase,
    ObservableCollection,
    PropertyChangedJoinPoint,
    Weaver
} from '@skylab-shared/core';
import { JpkVatCodes, ProductJpkVatCodes, SaleDocumentItemModel, SaleDocumentKind } from '@skylab-shared/dataModel';
import { Constructor } from '@skylab-shared/types';
import { not, sameValues } from '@skylab-shared/utils';
import { JpkVatProcedures } from '../../common/jpk-vat-procedures';
import { propertyChangedJoinPointFilter } from '../../common/property-changed/property-changed-filter';
import { CommunityTransactionForSaleQuery } from '../../community-transaction/queries/community-transaction-for-sale.query-base';
import { SaleJpkVatGroupCollectionQuery } from '../queries/sale-jpk-vat-group.query-base';
import { JpkGroupsMppLawChangesFlag } from '../sale-feature-flags-rules.service-base';
import { isAdjustment, isJpkGroupActive } from '../utils/sale-utils';
import { DocumentJpkEEShouldBeSelectedInWstoskTransactionError } from '../violations/document-jpk-ee-should-be-selected-in-wstosk-transaction-error';
import { DocumentJpkMppIsNotObligatoryWarning } from '../violations/document-jpk-mpp-is-not-obligatory-warning';
import { DocumentJpkSwIsNotObligatoryWarning } from '../violations/document-jpk-sw-is-not-obligatory-warning';
import { DocumentShouldNotHaveMoreThanOneSelectedJpkVatProcedureError } from '../violations/document-should-not-have-more-than-one-selected-jpk-vat-procedure-error';
import { JpkVatCodeShouldBeSelectedOnItemWarning } from '../violations/jpk-vat-code-should-be-selected-on-item-warning';

interface DocumentModelPart extends ModelBase {
    taxLiabilityDate: Date;
    issueDate: Date;
    jpkVatGroups: string[];
    kind: SaleDocumentKind;
    communityTransactionId: number;
    items: ObservableCollection<SaleDocumentItemModel>;
}

export class SaleDocumentJpkGroupsBoPart extends BusinessObjectPart<DocumentModelPart> {

    public constructor(
        weaver: Weaver,
        protected readonly featureFlagsRulesService: FeatureFlagsRulesServiceBase
    ) {
        super(weaver);
    }

    public override async initializeAsync(context: BusinessObjectPartContext<DocumentModelPart>): Promise<void> {
        await super.initializeAsync(context);
        const modelType = <Constructor<DocumentModelPart>> this.bo.model.constructor;
        const transactions = await this.bo.localContext.preload(CommunityTransactionForSaleQuery);
        const saleJpkGroups = await this.bo.localContext.preload(SaleJpkVatGroupCollectionQuery);

        this.engine.validation.addRule(
            [[modelType, 'jpkVatGroups'], [modelType, 'issueDate'], [modelType, 'taxLiabilityDate']],
            [[modelType, 'jpkVatGroups']],
            () => {
                const jpkGroups = <JpkVatCodes[]>this.bo.model.jpkVatGroups;
                if (!this.jpkGroupsMppLawChangesFlagEnabled || !jpkGroups) return true;
                return not(jpkGroups.some(elem => elem === 'SW'));
            },
            DocumentJpkSwIsNotObligatoryWarning
        );

        this.engine.validation.addRule(
            [[modelType, 'jpkVatGroups'], [modelType, 'issueDate'], [modelType, 'taxLiabilityDate']],
            [[modelType, 'jpkVatGroups']],
            () => {
                const jpkGroups = <JpkVatCodes[]>this.bo.model.jpkVatGroups;
                if (!this.jpkGroupsMppLawChangesFlagEnabled || !jpkGroups) return true;

                return not(jpkGroups.some(elem => elem === 'MPP'));
            },
            DocumentJpkMppIsNotObligatoryWarning
        );

        this.engine.validation.addRule(
            [[modelType, 'jpkVatGroups'], [modelType, 'communityTransactionId'], [modelType, 'taxLiabilityDate'], [modelType, 'issueDate']],
            [[modelType, 'jpkVatGroups']],
            () => {
                const wstoskId = transactions.find(elem => elem.symbol === 'WSTO-SK')?.id;
                if (this.bo.model.communityTransactionId !== wstoskId) return true;

                const dateToCompare = this.bo.model.taxLiabilityDate || this.bo.model.issueDate || new Date();
                if (!isJpkGroupActive(saleJpkGroups, JpkVatCodes.EE, dateToCompare)) return true;

                const groups = <JpkVatCodes[]>this.bo.model.jpkVatGroups;
                return !!groups?.find(elem => elem === JpkVatCodes.EE);
            },
            DocumentJpkEEShouldBeSelectedInWstoskTransactionError
        );

        this.engine.validation.addRule(
            [[modelType, 'jpkVatGroups']],
            [[modelType, 'jpkVatGroups']],
            () => {
                if (isAdjustment(this.bo.model.kind)) return true;

                const itemsJpkCodes =  Array.from(new Set(this.bo.model.items.map(elem => elem.jpkVatGroup).filter(elem => elem))).sort();
                const modelJpkCodes = this.bo.model.jpkVatGroups.filter(elem => Object.values(ProductJpkVatCodes).includes(elem as ProductJpkVatCodes)).sort();

                return (itemsJpkCodes.length === modelJpkCodes.length) && sameValues(itemsJpkCodes, modelJpkCodes);
            },
            JpkVatCodeShouldBeSelectedOnItemWarning
        );

        this.engine.validation.addRule(
            [[modelType, 'jpkVatGroups']],
            [[modelType, 'jpkVatGroups']],
            () => {
                const elements = this.bo.model.jpkVatGroups.filter(elem => JpkVatProcedures.includes(elem as JpkVatCodes));
                return not(elements.length > 1);
            },
            DocumentShouldNotHaveMoreThanOneSelectedJpkVatProcedureError
        );

        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(SaleDocumentItemModel, ['jpkVatGroups'])(
            () => this.engine.validation.queueValidation([this.modelType, 'jpkVatGroups'])
        )));
    }

    private get jpkGroupsMppLawChangesFlagEnabled(): boolean {
        const date = this.bo.model.kind === SaleDocumentKind.ProForma ? this.bo.model.issueDate : this.bo.model.taxLiabilityDate;
        return !date ? false : this.featureFlagsRulesService.getFlagStatus(JpkGroupsMppLawChangesFlag, date);
    }
}
